import { useRef, useState } from 'react';
import { Badge, Button, Group, Text } from '@mantine/core';
import { API_BASE, publicMessageOf } from '../lib/trpc';
import classes from '../pages/EntityPage.module.css';

/**
 * Одне медіа-поле форми: завантажити файл і покласти його id у значення форми.
 *
 * Два кроки, а не один, і це та сама причина, що й у слотах бренду: байти йдуть у
 * `POST /media/upload`, який єдиний їх санітизує (SVG зі скриптом і PNG із вантажем у хвості
 * — причина, чому прямого PUT у сховище не існує), і лише повернутий `mediaFileId` лягає в
 * поле. Тому один логотип може стояти в кількох полях, не дублюючись у сховищі.
 *
 * Компонент не зберігає сам: він віддає id через `onUploaded`, а запис іде разом із рештою
 * форми. Окреме збереження означало б, що логотип уже прикріплений, а назва ще ні — і
 * скасування форми лишало б половину змін.
 */

export interface MediaFileFieldProps {
  label: string;
  /** Текст вимог із мокапа — «SVG 120×40 світла», «PNG 512×512». */
  spec: string;
  /** Поточне значення: id файла або `''`, коли порожньо. */
  value: string;
  onUploaded: (mediaFileId: string) => void;
  onCleared: () => void;
  /** Ліміти й дозволені формати сервер бере зі слота; невідомий слот — загальна стеля. */
  slot?: string;
  disabled?: boolean;
  testId: string;
}

interface UploadResult {
  mediaFileId?: string;
  message?: string;
  error?: string;
}

export function MediaFileField({
  label,
  spec,
  value,
  onUploaded,
  onCleared,
  slot,
  disabled = false,
  testId,
}: MediaFileFieldProps): React.JSX.Element {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      if (slot !== undefined) body.append('slot', slot);
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      const json = (await res.json()) as UploadResult;
      if (!res.ok || json.mediaFileId === undefined) {
        setError(json.message ?? json.error ?? 'Не вдалося завантажити файл.');
        return;
      }
      onUploaded(json.mediaFileId);
    } catch (err) {
      setError(publicMessageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Group gap={8} align="center" data-testid={testId}>
      <Text size="sm" style={{ width: 150 }}>
        {label}
      </Text>
      {/*
        * Порожній слот — робочий стан, а не помилка: довідник без темного логотипа працює,
        * просто на темній темі візьме світлий. Тому «немає» нейтральне, а не червоне.
        */}
      <Badge size="xs" color={value === '' ? 'slate' : 'mint'} data-testid={`${testId}-state`}>
        {value === '' ? 'немає' : 'є'}
      </Badge>
      <Text className={classes.dim} style={{ flex: 1 }}>
        {spec}
      </Text>
      <input
        ref={input}
        type="file"
        hidden
        data-testid={`${testId}-input`}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file !== undefined) void upload(file);
          event.currentTarget.value = '';
        }}
      />
      <Button
        size="compact-xs"
        variant="default"
        loading={busy}
        disabled={disabled}
        data-testid={`${testId}-upload`}
        onClick={() => {
          input.current?.click();
        }}
      >
        {value === '' ? 'Завантажити' : 'Замінити'}
      </Button>
      {value === '' ? null : (
        <Button
          size="compact-xs"
          variant="subtle"
          disabled={disabled}
          data-testid={`${testId}-clear`}
          onClick={onCleared}
        >
          Прибрати
        </Button>
      )}
      {error === null ? null : (
        <Text className={classes.danger} data-testid={`${testId}-error`} role="alert">
          {error}
        </Text>
      )}
    </Group>
  );
}
